<script>
	import { onDestroy } from "svelte";
	import { get } from "svelte/store";
	import { navigate } from "svelte-routing";
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import CustomTableBadge from "src/components/CustomTable/CustomTableBadge.svelte";
	import ConfirmModal from "src/components/CustomModal/ConfirmModal.svelte";
	import Loader from "src/components/Loader/Loader.svelte";
	import { convert } from "src/services/helpers";
	import { getVehicleById } from "src/services/maintenanceRepair";
	import { lockUnlockCar } from "src/services/vehicle";
	import {
		buttonLoading,
		errorMessage,
		toastMessage,
		userRole,
	} from "src/store/store";
	import {
		daysElapsed,
		extractErrorMessage,
		formatErrorMessage,
		scrollToTop,
	} from "src/utils/misc";
	import { vehicleStatusVariantByLabel } from "src/utils/options";

	export let vehicleId;

	// The car management detail page is only routed for userRole 2
	// (Layout.svelte), which M&R users (role 13) don't necessarily have.
	const CAR_MANAGEMENT_ROLE = 2;

	// errorMessage is a global store that other pages also write to. Start
	// clean so a stale error from elsewhere doesn't show here. (It's cleared
	// again on destroy — see the onDestroy below fetchVehicle.)
	errorMessage.set("");

	// Mirrors CarDetailsCard.svelte, which hides Lock/Unlock for
	// vehicleStatusId 4 (Decommissioned) and 5 (Scrapped). Whether M&R should
	// use the same guard is still open (spec A-42).
	const LOCK_UNLOCK_HIDDEN_STATUSES = [
		"Inactive - Decommissioned",
		"Inactive - Scrapped",
	];

	let vehicle = null;
	// "loading" | "loaded" | "error"
	let loadStatus = "loading";

	// Lock/Unlock follows CarDetail.svelte: a confirm modal collects a reason,
	// then lockUnlockCar() posts it to the vehicle service.
	const modalDialog = {
		lock: {
			header: "Lock vehicle",
			bodyText:
				"You are about to lock this vehicle. Please provide a reason for locking.",
		},
		unlock: {
			header: "Unlock vehicle",
			bodyText:
				"You are about to unlock this vehicle. Please provide a reason for unlocking.",
		},
	};

	let isModalOpen = false;
	let action = "";
	let selectedOption = "";
	let otherReason = "";

	$: options = [
		{
			value: `Unable to ${action} via Operator App`,
			label: `Unable to ${action} via Operator App`,
		},
		{ value: "Request from workshop", label: "Request from workshop" },
		{ value: "Others", label: "Others" },
	];

	let isSubmitting = false;
	// Bumped whenever an in-flight lock/unlock stops belonging to what's on
	// screen: the vehicle changed without a remount, or the tab closed.
	let submitGeneration = 0;

	function resetModal() {
		action = "";
		selectedOption = "";
		otherReason = "";
	}

	// ConfirmModal calls this to open (with an action) and to cancel.
	function toggle(actionType = "") {
		isModalOpen = !isModalOpen;
		resetModal();
		action = actionType;
	}

	function closeModal() {
		isModalOpen = false;
		resetModal();
	}

	// A lock/unlock in flight no longer matches what's on screen (the vehicle
	// changed without a remount, or the tab is closing): detach from it. Only
	// if one is actually pending do we stop waiting on it (so Confirm works
	// again) and release the shared buttonLoading flag lockUnlockCar holds
	// until it settles — ConfirmModal disables Confirm from that flag, and
	// other pages read it too. Releasing it when nothing of ours is pending
	// could clear someone else's loading state.
	function detachPendingSubmit() {
		submitGeneration++;
		if (isSubmitting) {
			isSubmitting = false;
			buttonLoading.set(false);
		}
	}

	// On a vehicle change, also close any modal opened for the old vehicle, so
	// it can't be confirmed against the new one.
	function abandonPendingSubmit() {
		detachPendingSubmit();
		closeModal();
	}

	async function handleSubmit() {
		// ConfirmModal submits on Enter as well as on the Confirm click, so the
		// same confirmation can arrive twice. Only send one request.
		if (isSubmitting) return;
		isSubmitting = true;
		const generation = ++submitGeneration;
		const submittedAction = action;
		const isStillCurrent = () => generation === submitGeneration;
		try {
			const remarks =
				selectedOption === "Others" ? otherReason : selectedOption;
			const response = await lockUnlockCar(
				submittedAction,
				vehicleId,
				remarks
			);
			if (response.status === 200) {
				// The toast is global, so it's correct even if the vehicle or tab
				// has changed since.
				toastMessage.set({ type: "success", message: "Request sent." });
			}
		} catch (error) {
			// lockUnlockCar has already put the backend's reason into
			// errorMessage, so keep it rather than replacing it with a generic
			// one.
			console.error(error);
			if (isStillCurrent()) {
				setTimeout(() => {
					scrollToTop();
				}, 300);
			} else {
				// The vehicle changed or the tab closed mid-request, so that banner
				// would sit on the wrong vehicle or the next page. Move the failure
				// to the global toast instead of dropping it. The reason comes from
				// the error itself: by now the shared errorMessage store may hold
				// something newer.
				const reason =
					formatErrorMessage(error) ||
					`Vehicle ${submittedAction} was unsuccessful`;
				// Only clear the banner if it still shows this failure.
				if (get(errorMessage) === reason) errorMessage.set("");
				toastMessage.set({ type: "danger", message: reason });
			}
		} finally {
			if (isStillCurrent()) {
				isSubmitting = false;
				// Close explicitly rather than toggling, so a late second call
				// can never reopen the modal.
				closeModal();
			}
		}
	}

	let currentVehicleId = vehicleId;
	$: if (vehicleId !== currentVehicleId) {
		currentVehicleId = vehicleId;
		abandonPendingSubmit();
	}

	let latestRequestId = 0;
	// The message this tab's last failed load put into errorMessage, so a later
	// successful load can clear it without wiping an unrelated error.
	let lastLoadError = "";

	async function fetchVehicle(id) {
		const requestId = ++latestRequestId;
		loadStatus = "loading";
		// Switching to a different vehicle: don't keep showing the previous one
		// while Lock/Unlock would already act on the new id.
		if (vehicle && String(vehicle.vehicleId) !== String(id)) vehicle = null;
		try {
			const res = await getVehicleById(id);
			// Only the most recent request may update the card, so a slower,
			// earlier response (e.g. a double-clicked refresh) can't overwrite it.
			if (requestId !== latestRequestId) return;
			vehicle = res.data ?? null;
			loadStatus = "loaded";
			if (lastLoadError && get(errorMessage) === lastLoadError) {
				errorMessage.set("");
			}
			lastLoadError = "";
		} catch (error) {
			if (requestId !== latestRequestId) return;
			console.error(error);
			// On a failed refresh the last good details stay on screen, with the
			// error shown above them.
			extractErrorMessage(error);
			lastLoadError = get(errorMessage);
			loadStatus = "error";
		}
	}

	onDestroy(() => {
		// Invalidate anything in flight: a vehicle request can't write its data
		// or error after the tab has gone, and a lock/unlock failure is moved to
		// the toast.
		latestRequestId++;
		detachPendingSubmit();
		// Clear on the way out so an error from this tab doesn't leak onto the
		// next page.
		errorMessage.set("");
	});

	// Same helper as the landing page's "Days in workshop" column, so the two
	// screens can't disagree.
	$: daysInWorkshop = vehicle?.enteredWorkshopAt
		? String(daysElapsed(vehicle.enteredWorkshopAt))
		: "-";

	$: showLockUnlockButtons =
		!!vehicle &&
		!LOCK_UNLOCK_HIDDEN_STATUSES.includes(vehicle.vehicleStatus);

	$: canOpenCarDetail = ($userRole ?? []).includes(CAR_MANAGEMENT_ROLE);

	// Fetch on mount and again whenever the vehicle changes. vehicleId is passed
	// explicitly because Svelte only tracks variables named in the reactive
	// statement, and the tab can stay mounted across a vehicle change (e.g.
	// back/forward between two vehicle URLs).
	$: fetchVehicle(vehicleId);
</script>

{#if !vehicle && loadStatus === "loading"}
	<Loader />
{:else if !vehicle && loadStatus === "loaded"}
	<p class="empty-message">Vehicle not found.</p>
{/if}

{#if vehicle}
	<div class="vehicle-details-card">
		<Row class="details-row">
			<Col xs={4}>
				<div class="details-label">Vehicle plate number</div>
				{#if canOpenCarDetail}
					<button
						type="button"
						class="details-link link link-text"
						on:click={() =>
							navigate(
								`/car-management/details/${vehicle.vehicleId}`
							)}
					>
						{vehicle.plateNumber}
					</button>
				{:else}
					<div>{vehicle.plateNumber}</div>
				{/if}
			</Col>
			<Col xs={4}>
				<div class="details-label">Make and model</div>
				<div>{vehicle.makeAndModel || "-"}</div>
			</Col>
			<Col xs={4}>
				<div class="details-label">Platform</div>
				<div>{vehicle.platform || "-"}</div>
			</Col>
		</Row>

		<Row class="details-row">
			<Col xs={4}>
				<div class="details-label">Last updated</div>
				<div>{convert(vehicle.lastUpdatedAt) || "-"}</div>
			</Col>
			<Col xs={4}>
				<div class="details-label">Home lot address</div>
				<div>{vehicle.homeLotAddress || "-"}</div>
			</Col>
			<Col xs={4}>
				<div class="details-label">
					Current location
					{#if vehicle.currentLocationUpdatedAt}
						(At {convert(
							vehicle.currentLocationUpdatedAt,
							"DD/MM/YYYY HH:mm"
						)})
					{/if}
					<button
						type="button"
						class="refresh-button"
						title="Refresh current location"
						on:click={() => fetchVehicle(vehicleId)}
					>
						<img
							src="/assets/icon-rotate.svg"
							alt=""
							width="16"
							height="16"
						/>
						<span class="visually-hidden"
							>Refresh current location</span
						>
					</button>
				</div>
				<div>{vehicle.currentLocation || "-"}</div>
			</Col>
		</Row>

		<Row class="details-row">
			<Col xs={4}>
				<div class="details-label">Days in workshop</div>
				<div>{daysInWorkshop}</div>
			</Col>
			<Col xs={4}>
				<div class="details-label">Input by</div>
				<div>{vehicle.inputBy || "-"}</div>
			</Col>
			<Col xs={4}>
				<div class="details-label">Vehicle status</div>
				{#if vehicle.vehicleStatus}
					<CustomTableBadge
						row={vehicle}
						key="vehicleStatus"
						options={vehicleStatusVariantByLabel}
					/>
				{:else}
					<div>-</div>
				{/if}
			</Col>
		</Row>
	</div>

	{#if showLockUnlockButtons}
		<div class="d-flex mt-4 gap-4">
			<Button
				class="themeButton backButton px-4"
				on:click={() => toggle("lock")}
			>
				Lock vehicle
			</Button>
			<Button
				class="themeButton backButton px-4"
				on:click={() => toggle("unlock")}
			>
				Unlock vehicle
			</Button>
		</div>
	{/if}

	<ConfirmModal
		header={modalDialog[action]?.header}
		bodyText={modalDialog[action]?.bodyText}
		buttonColor="primary"
		open={isModalOpen}
		{toggle}
		isDropdownEnabled={true}
		{options}
		{action}
		{handleSubmit}
		bind:selectedOption
		bind:otherReason
	/>
{/if}

<style>
	.empty-message {
		font-weight: bold;
	}

	.vehicle-details-card {
		border: 1px solid var(--fds-outline-variant);
		border-radius: 8px;
		padding: 0 24px;
		font-size: 14px;
	}

	.vehicle-details-card :global(.details-row) {
		padding: 24px 0;
	}

	.vehicle-details-card :global(.details-row + .details-row) {
		border-top: 1px solid var(--fds-outline-variant);
	}

	.details-label {
		color: var(--fds-on-surface-variant);
		font-size: 14px;
		margin-bottom: 4px;
	}

	.details-link {
		background: none;
		border: none;
		padding: 0;
	}

	.refresh-button {
		background: none;
		border: none;
		padding: 0;
		margin-left: 6px;
		cursor: pointer;
		display: inline-flex;
		vertical-align: middle;
	}
</style>
